import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { JobPostingsTable } from "@/components/JobPostingsTable";
import { JobApplicationsTable } from "@/components/JobApplicationsTable";
import { CreateJobDialog } from "@/components/CreateJobDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Briefcase, Users } from "lucide-react";

const Jobs = () => {
  const navigate = useNavigate();
  const [createJobDialogOpen, setCreateJobDialogOpen] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
      }
    };

    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate("/login");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const { data: jobPostings, isLoading: jobsLoading, refetch: refetchJobs } = useQuery({
    queryKey: ['job-postings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_postings')
        .select(`
          *,
          job_applications(count)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const { data: jobApplications, isLoading: applicationsLoading } = useQuery({
    queryKey: ['job-applications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_applications')
        .select(`
          *,
          job_postings (
            title,
            department
          )
        `)
        .order('applied_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl md:text-3xl font-bold">Jobs Dashboard</h1>
            <Button onClick={() => setCreateJobDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Job Posting
            </Button>
          </div>

          <Tabs defaultValue="postings" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="postings" className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Job Postings ({jobPostings?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="applications" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Applications ({jobApplications?.length || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="postings">
              <JobPostingsTable 
                jobPostings={jobPostings || []} 
                isLoading={jobsLoading}
                onRefetch={refetchJobs}
              />
            </TabsContent>

            <TabsContent value="applications">
              <JobApplicationsTable 
                jobApplications={jobApplications || []} 
                isLoading={applicationsLoading}
              />
            </TabsContent>
          </Tabs>

          <CreateJobDialog
            isOpen={createJobDialogOpen}
            onClose={() => setCreateJobDialogOpen(false)}
            onJobCreated={refetchJobs}
          />
        </div>
      </div>
    </div>
  );
};

export default Jobs;